import { useQuery } from "@tanstack/solid-query";
import { isServer } from "solid-js/web";
import { useWire } from "~/wire";
import { authTokenReady } from "~/wire/authTokenReady";

// Shared prefix so a run trigger can invalidate every consumer of an action's
// executions (card limit and detail-page limit) with one call.
export const actionExecutionsKey = (actionId: string) => ["action-executions", actionId] as const;

// Run history is a plain query, refreshed on demand: consumers read it, and the
// run-trigger handlers invalidate actionExecutionsKey(id) so the new run shows
// up without a manual reload. Deliberately no interval/focus polling — driving
// refetches from timers or the tab-focus event raced the auth-token refresh and
// crashed <Show> with a stale value (ENG-6720).
export const useActionExecutions = (actionId: () => string | undefined, limit: number) => {
  const wire = useWire();

  const query = useQuery(() => {
    const id = actionId();
    return {
      queryKey: [...actionExecutionsKey(id ?? ""), limit],
      enabled: !isServer && authTokenReady(wire.auth) && !!wire.auth.userId() && !!id,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      queryFn: async () => {
        const result = await wire.actions.getExecutions({ actionIds: [id as string], limit });
        if (result.isErr()) throw result.error;
        return result.value.records;
      },
    };
  });

  const executions = () => (query.isSuccess || query.isRefetchError ? query.data : []);
  const executionsLoading = () => query.isLoading;

  return { executions, executionsLoading };
};
